import { AxiosResponse } from 'axios';

import axiosInstance from 'api/base';
import { normalizeBaseGist, normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { prepareFormData } from 'utils/form';
import { IAllPageFilters, IBaseGist, INullable, IPageType, TObject } from 'utils/types';

import {
  normalizeLegalEntity,
  normalizeOrganization,
  normalizeOrganizationLegalEntity,
  normalizerOrganizationDocument,
} from './normalizers';
import {
  ILegalEntity,
  ILegalEntityCreatePayload,
  ILegalEntityDocCreatePayload,
  ILegalEntityEditPayload,
  IOrganization,
  IOrganizationDocuments,
  IOrganizationDocumentsData,
  IOrganizationForm,
  IOrganizationLegalEntity,
} from './types';

const organizationsAPI = {
  async getAllOrganizations(filters: IAllPageFilters): Promise<IPageType<IOrganization>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const { data } = await axiosInstance.get('merchants', {
      params: normalizedFilter,
    });

    return normalizePageData<TObject, IOrganization>(
      data,
      normalizeOrganization,
      JSON.stringify(filters.search),
    );
  },

  async getOrganizationDetails(id: number | string): Promise<IOrganization> {
    const response = await axiosInstance.get(`merchants/${id}`);

    return normalizeOrganization(response.data);
  },

  async createOrganization(payload: IOrganizationForm): Promise<IOrganization> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.post(`merchants`, normalizedPayload);

    return normalizeOrganization(response.data);
  },

  async updateOrganization({
    id,
    ...payload
  }: IOrganizationForm): Promise<IOrganization> {
    const normalizedPayload = prepareFormData(payload);

    const response = await axiosInstance.put(`merchants/${id}`, normalizedPayload);

    return normalizeOrganization(response.data);
  },

  async deleteOrganization(id: string): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`merchants/${id}`);

    return response.data;
  },

  async getAllOrganizationDocuments(
    filters: IAllPageFilters,
    id?: string,
  ): Promise<IPageType<IOrganizationDocuments>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get(`merchants/${id}/legal-entities/documents`, {
      params: normalizedFilter,
    });

    return normalizePageData<IOrganizationDocumentsData, IOrganizationDocuments>(
      response.data,
      normalizerOrganizationDocument,
      JSON.stringify(filters.search),
    );
  },

  async getDocumentFileLink(id: number): Promise<string> {
    const response = await axiosInstance.get<Blob>(
      `merchants/legal-entities/documents/${id}`,
      {
        responseType: 'blob',
      },
    );
    return URL.createObjectURL(response.data);
  },

  async getOrganizationLegalEntities(
    filters: IAllPageFilters,
    id?: INullable<number | string>,
  ): Promise<IPageType<IOrganizationLegalEntity>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await axiosInstance.get(`merchants/${id}/legal-entities`, {
      params: normalizedFilter,
    });

    return normalizePageData<TObject, IOrganizationLegalEntity>(
      response.data,
      normalizeOrganizationLegalEntity,
      JSON.stringify(filters.search),
    );
  },

  async getLegalEntity(
    id?: INullable<number | string>,
  ): Promise<IOrganizationLegalEntity> {
    const response = await axiosInstance.get(`merchants/legal-entities/${id}`);

    return normalizeOrganizationLegalEntity(response.data);
  },

  async createLegalEntity({
    merchantId,
    ...payload
  }: ILegalEntityCreatePayload): Promise<IBaseGist> {
    const response = await axiosInstance.post(
      `merchants/${merchantId}/legal-entities`,
      payload,
    );

    return normalizeBaseGist(response?.data);
  },

  async updateLegalEntity({
    id,
    ...payload
  }: ILegalEntityEditPayload): Promise<IOrganizationLegalEntity> {
    const response = await axiosInstance.put(`merchants/legal-entities/${id}`, payload);

    return normalizeOrganizationLegalEntity(response.data);
  },

  async createLegalEntityDoc(payload: ILegalEntityDocCreatePayload): Promise<IBaseGist> {
    const { description, id, type, ...body } = payload;

    const response = await axiosInstance.post(
      `merchants/legal-entities/documents`,
      body.file,
      {
        params: {
          description,
          id,
          type,
        },
      },
    );

    return normalizeBaseGist(response?.data);
  },

  async deleteLegalEntityDoc(id: number): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(
      `merchants/legal-entities/documents/${id}`,
    );

    return response;
  },

  async getLegalEntityNames(id: number | string): Promise<ILegalEntity[]> {
    const response = await axiosInstance.get(`merchants/${id}/legal-entities/names`);

    return response?.data?.result?.map(normalizeLegalEntity) || [];
  },

  async deleteLegalEntity(id: number): Promise<AxiosResponse> {
    const response = await axiosInstance.delete(`merchants/legal-entities/${id}`);

    return response.data;
  },
};

export default organizationsAPI;
